import { Button, Card, Chip } from "@heroui/react";
import { Check, Clipboard, Link } from "lucide-react";
import React, { useState, useEffect } from "react";

const codeBlock = (str) => (
  <pre className="mt-2 w-full overflow-auto rounded-lg bg-default-100 text-glass-foreground text-sm leading-relaxed p-3">
    <code>{str}</code>
  </pre>
);

const METHOD_STYLES = {
  GET: "bg-success/10 text-success",
  POST: "bg-accent/10 text-accent",
  PATCH: "bg-warning/10 text-warning",
  DELETE: "bg-danger/10 text-danger",
};

const METHOD_COLORS = {
  GET: "success",
  POST: "accent",
  PATCH: "warning",
  DELETE: "danger",
};

const DocsEndpointCard = ({ endpoint }) => {
  const { id, method, path, curl } = endpoint;

  const [linkCopied, setLinkCopied] = useState(false);
  const [curlCopied, setCurlCopied] = useState(false);

  const methodClassName = METHOD_STYLES[method] ?? "bg-default text-default-foreground";

  const [isDark, setIsDark] = useState(() => document.documentElement.classList.contains("dark"));

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains("dark"));
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);

  // Handler for copying the shareable link with hash
  const handleCopyLink = async () => {
    if (!id) return;

    const url = `${window.location.origin}${window.location.pathname}#${id}`;

    try {
      await navigator.clipboard.writeText(url);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy link:", err);
    }
  };

  // Handler for copying the curl command
  const handleCopyCurl = async () => {
    if (!curl) return;

    try {
      await navigator.clipboard.writeText(curl);
      setCurlCopied(true);
      setTimeout(() => setCurlCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy curl:", err);
    }
  };

  return (
    <Card id={id} className="glass-surface dark:border-white/20! dark:border-2">
      <Card.Header className="flex-row justify-between items-center">
        <div className="flex flex-row gap-2">
          <Chip variant={isDark ? "primary" : 'soft'} color= {METHOD_COLORS[method]}>
            <Chip.Label>{method}</Chip.Label>
          </Chip>

          <Chip>
            <Chip.Label className="font-mono">{path}</Chip.Label>
          </Chip>
        </div>

        <div className="flex gap-1">
          <Button
            isIconOnly
            size="sm"
            variant="ghost"
            className="text-accent dark:text-white"
            onPress={handleCopyLink}
            aria-label="Copy link to this endpoint"
          >
            {linkCopied ? <Check /> : <Link />}
          </Button>

          <Button
            isIconOnly
            size="sm"
            variant="ghost"
            className="text-accent dark:text-white"
            onPress={handleCopyCurl}
            aria-label="Copy curl command"
          >
            {curlCopied ? <Check /> : <Clipboard />}
          </Button>
        </div>
      </Card.Header>

      <Card.Content>
        <div>
          <h3 className="font-bold text-xl text-glass-foreground">{endpoint.title}</h3>
          <p className="text-xs text-black/40 dark:text-white/60">{endpoint.description}</p>
        </div>

        {codeBlock(curl)}
      </Card.Content>
    </Card>
  );
};

export default DocsEndpointCard;
